import {
    useEffect,
    useMemo,
    type FC,
    useRef
} from "react";
import {
    Animated,
    Platform,
    View
} from "react-native";
import type ILoadingProps from "./type";
import stylesheet, {
    LOADING_BRAND_STROKE_WIDTH,
    LOADING_BRAND_VIEWBOX,
    LOADING_BRAND_PATH,
    getLoadingColor,
    getLoadingSize,
    useStyles
} from "./stylesheet";
import LoadingDots from "./components/dots";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    Loader as LoaderIcon
} from "lucide-react-native";
import {
    Path,
    Svg
} from "react-native-svg";
import {
    MOTION_EASINGS
} from "../../helpers/motion";

const AnimatedView = Platform.OS === "web" ? View : Animated.View;

const LoadingIcon: FC<ILoadingProps> = ({
    "aria-hidden": ariaHidden,
    "aria-label": ariaLabel,
    accessibilityLabel,
    color = "default",
    variant = "icon",
    size = "medium",
    customLocalize,
    customColor,
    customTheme,
    style,
    ...props
}) => {
    const {
        durations,
        iconSizes,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const rotation = useRef(new Animated.Value(0)).current;

    const rotate = useMemo(() => rotation.interpolate({
        outputRange: [
            "0deg",
            "360deg"
        ],
        inputRange: [
            0,
            1
        ]
    }), [
        rotation
    ]);

    const currentSize = getLoadingSize({
        iconSizes,
        size
    });

    const currentColor = getLoadingColor({
        customColor,
        colors,
        color
    });

    const isBrand = variant === "brand";

    const spinDuration = isBrand ? durations.loop : durations.spin;

    const isWeb = Platform.OS === "web";

    const {
        container: containerDynamicStyle,
        webSpin: webSpinDynamicStyle
    } = useStyles({
        size: currentSize,
        spinDuration
    });

    useEffect(() => {
        rotation.setValue(0);

        if(!spinDuration || isWeb) {
            return;
        }

        const animation = Animated.loop(Animated.timing(rotation, {
            easing: MOTION_EASINGS.linear,
            duration: spinDuration,
            useNativeDriver: true,
            isInteraction: false,
            toValue: 1
        }));

        animation.start();

        return () => {
            animation.stop();
        };
    }, [
        spinDuration,
        rotation,
        isWeb
    ]);

    return <View
        {...props}
        aria-label={ariaHidden ? undefined : ariaLabel ?? accessibilityLabel ?? localize("loading")}
        role={ariaHidden ? undefined : "status"}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        aria-hidden={ariaHidden}
    >
        <AnimatedView
            style={isWeb ? [
                stylesheet.webSpin,
                webSpinDynamicStyle
            ] : {
                transform: [
                    {
                        rotate
                    }
                ]
            }}
            aria-hidden={true}
        >
            {
                isBrand ?
                    <Svg
                        viewBox={LOADING_BRAND_VIEWBOX}
                        height={currentSize}
                        width={currentSize}
                        fill="none"
                    >
                        <Path
                            strokeWidth={LOADING_BRAND_STROKE_WIDTH}
                            d={LOADING_BRAND_PATH}
                            strokeLinejoin="round"
                            strokeLinecap="round"
                            stroke={currentColor}
                        />
                    </Svg>
                :
                    <LoaderIcon
                        color={currentColor}
                        size={currentSize}
                    />
            }
        </AnimatedView>
    </View>;
};

const Loading: FC<ILoadingProps> = ({
    variant = "icon",
    customLocalize,
    pattern,
    surface,
    type,
    ...props
}) => {
    if(variant === "dots") {
        return <LoadingDots
            {...props}
            customLocalize={customLocalize}
            surface={surface}
            pattern={pattern}
            type={type}
        />;
    }

    return <LoadingIcon
        {...props}
        customLocalize={customLocalize}
        variant={variant}
    />;
};
export default Loading;
